/**
 * 業務ロジック（`getBusinessRules`）と業務ルール 1 件（`getRule`）の TanStack Query ラッパー。
 *
 * 画面が `rpcClient` を直接叩かないための層である。Query キーと入力の対応をここだけで決める。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務 1 件の業務ルールの集合と、業務ルール 1 件の中身を TanStack Query のフックとして画面へ供給する。
 * @graph-connects rpcClient [calls] specGraph.getBusinessRules
 * @graph-connects rpcClient [calls] specGraph.getRule
 */
import type { GetBusinessRulesOutput, GetRuleOutput } from '@cortex/domain-spec-console';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { useSpecLocale } from '../../i18n/spec-locale.js';
import { rpcClient } from '../../lib/orpc.js';
import { queryKeys } from '../../lib/query-keys.js';

/**
 * 業務 1 件の業務ルールの集合・依存・検査・次の業務を取得する。
 *
 * **投げない選択を呼び出し側に渡す。** 案件が新設した業務にはマスターの姿が無いので、突き合わせの
 * 変更前として引くと必ず未存在で返る。呼び出し側は案件の変更の行からそれを先に知れる。
 *
 * @param businessId 業務の番号
 * @param pjNumber 重ねる案件の番号。マスターを読むなら省く
 * @param enabled 取得してよいか。省けば取得する
 * @returns 業務 1 件の業務ロジック
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務 1 件の業務ロジックを、マスターまたは指定した案件を重ねた姿として取得する。
 * @graph-connects rpcClient [calls] specGraph.getBusinessRules
 */
export function useBusinessRules(
  businessId: number,
  pjNumber?: number,
  enabled = true,
): UseQueryResult<GetBusinessRulesOutput> {
  const locale = useSpecLocale();
  return useQuery({
    queryKey: queryKeys.specGraph.getBusinessRules(businessId, locale, pjNumber),
    queryFn: () =>
      rpcClient.specGraph.getBusinessRules({
        businessId,
        locale,
        ...(pjNumber === undefined ? {} : { pjNumber }),
      }),
    enabled: enabled && businessId > 0,
  });
}

/**
 * 業務ルール 1 件を master と案件適用後の姿で取得する。
 *
 * @param ruleId 業務ルールの番号
 * @param pjNumber 重ねる案件の番号。マスターを読むなら省く
 * @returns 変更前（master）と変更後（案件適用後）の 2 つの姿
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務ルール 1 件を、master と指定した案件を適用した姿の両方として取得する。
 * @graph-connects rpcClient [calls] specGraph.getRule
 */
export function useRule(ruleId: number, pjNumber?: number): UseQueryResult<GetRuleOutput> {
  const locale = useSpecLocale();
  return useQuery({
    queryKey: queryKeys.specGraph.getRule(ruleId, locale, pjNumber),
    queryFn: () =>
      rpcClient.specGraph.getRule({
        ruleId,
        locale,
        ...(pjNumber === undefined ? {} : { pjNumber }),
      }),
    enabled: Number.isInteger(ruleId) && ruleId > 0,
  });
}
